/**
 * Salesforce <code>MerchAccPaymentMethodSet</code> object containing payment methods to present, and vendor
 * information to use to create user interface controls and confirm payments for those payment methods.
 * @public
 */
export interface PaymentMethodSet {
  paymentMethodSetAccounts: VendorAccount[];
  paymentMethods: PaymentMethod[];
}

export interface VendorAccount {
  accountId: string;
  config: VendorConfiguration;
  gatewayId: string;
  vendor: Vendor;
  isLive?: boolean;
}

export interface PaymentMethod {
  accountId: string;
  paymentMethodType: PaymentMethodType;
  paymentModes: PaymentMode[];
}

export interface VendorConfiguration {
  key: string;
  bnCode?: string;
  paymentMethods?: Record<string, any>;
  country?: string;
}

export enum Vendor {
  STRIPE = 'Stripe',
  PAYPAL = 'Paypal',
  ADYEN = 'Adyen',
}

export enum PaymentMethodType {
  APPLEPAY = 'applepay',
  AFTERPAY_CLEARPAY = 'afterpay_clearpay',
  AFTERPAY_CLEARPAY_MESSAGE = 'afterpay_clearpay-message',
  AU_BECS_DEBIT = 'au_becs_debit',
  BANCONTACT = 'bancontact',
  BANCONTACT_MOBILE = 'bancontact_mobile', // Applicable for Adyen only
  BACS_DEBIT = 'bacs_debit',
  CARD = 'card',
  EPS = 'eps',
  GOOGLEPAY = 'googlepay',
  IDEAL = 'ideal',
  KLARNA = 'klarna',
  KLARNA_PAY_NOW = 'klarna_paynow', // Applicable for Adyen only
  KLARNA_ACCOUNT = 'klarna_account', // Applicable for Adyen only
  PAYMENT_REQUEST = 'paymentrequest',
  PAYPAL = 'paypal',
  PAYPAL_EXPRESS = 'paypal-express',
  PAYPAL_MESSAGE = 'paypal-message',
  SEPA_DEBIT = 'sepa_debit',
  US_BANK_ACCOUNT = 'us_bank_account',
  VENMO = 'venmo',
  TWINT = 'twint',
}

export enum PaymentMethodElement {
  STRIPE_APPLEPAY = 'Stripe.applepay',
  STRIPE_AFTERPAY_CLEARPAY = 'Stripe.afterpay_clearpay',
  STRIPE_AU_BECS_DEBIT = 'Stripe.au_becs_debit',
  STRIPE_BANCONTACT = 'Stripe.bancontact',
  STRIPE_BACS_DEBIT = 'Stripe.bacs_debit',
  STRIPE_CARD = 'Stripe.card',
  STRIPE_EPS = 'Stripe.eps',
  STRIPE_GOOGLEPAY = 'Stripe.googlepay',
  STRIPE_IDEAL = 'Stripe.ideal',
  STRIPE_KLARNA = 'Stripe.klarna',
  STRIPE_PAYMENT_REQUEST = 'Stripe.paymentrequest',
  PAYPAL_PAYPAL = 'Paypal.paypal',
  PAYPAL_PAYPAL_EXPRESS = 'Paypal.paypal-express',
  STRIPE_SEPA_DEBIT = 'Stripe.sepa_debit',
  STRIPE_US_BANK_ACCOUNT = 'Stripe.us_bank_account',
  PAYPAL_VENMO = 'Paypal.venmo',
  STRIPE_EXPRESS = 'Stripe.express',
  STRIPE_REDIRECT = 'Stripe.redirect',
  STRIPE_PAYMENT_APP = 'Stripe.payment_app',
  STRIPE_PAYMENT_ELEMENT = 'Stripe.payment_element',
  ADYEN_WEB = 'Adyen.Web',
  ADYEN_SAVABLE_PAYMENT_METHOD = 'Adyen.Savable_payment_method',
  ADYEN_EXPRESS_GOOGLEPAY = 'Adyen.express.googlepay',
  ADYEN_EXPRESS_APPLEPAY = 'Adyen.express.applepay',
}

/**
 * Express checkout use case.
 * @property {number} BUY_NOW - <code>0</code>
 * @property {number} PAY_NOW - <code>1</code>
 * @public
 */
export enum ExpressType {
  BUY_NOW,
  PAY_NOW,
}

export enum PaymentMode {
  MULTISTEP = 'Multistep',
  EXPRESS = 'Express',
}

export enum ComponentType {
  EXPRESS,
  CHECKOUT,
  SETUP,
  MESSAGES,
}
